/**
 * All Tasks — ADMIN task-oversight screen (Build B · 2026-05-21).
 *
 * ─────────────────────────────────────────────────────────────────────────
 *  WHY
 * ─────────────────────────────────────────────────────────────────────────
 *  The conversational AI assistant — in-app and over WhatsApp — CREATES
 *  real `tasks/` Firestore documents (one shared system, the
 *  `StaffResolver` contract). Until Build B NO portal screen read them: the
 *  owner had no way to see who had been assigned what, or how much.
 *
 *  This screen reads the REAL top-level `tasks/` collection (live, via
 *  `lib/tasksStore.listenTasks`) and presents it grouped by assignee — the
 *  busiest assignee first — with a status filter (open / done / all) and an
 *  assignee filter. Admin-only: the route is gated `roles=['admin']` in
 *  App.tsx, and the capability key `tasks.view.all` is admin-only.
 *
 *  Visual system: the Build-A depth tokens (`--rule`, `--icon`, `--e-1/2`)
 *  + the shared Box / Card / Tag / EmptyNote primitives — matches the
 *  existing admin screens (Workload / Money). No new CSS.
 * ─────────────────────────────────────────────────────────────────────────
 */
import { useEffect, useMemo, useState } from 'react';
import { useAuth } from '../lib/auth';
import { visibilityFor } from '../lib/roles';
import { useToast } from '../components/Toast';
import { Box, Card, EmptyNote, Pill, Tag } from '../components/ui';
import { ListLoading } from '../components/ListState';
import {
  listenTasks,
  groupByAssignee,
  isOpenTask,
  openCount,
  UNASSIGNED_UID,
  type LiveTask,
} from '../lib/tasksStore';

type StatusFilter = 'open' | 'done' | 'all';

function matchesStatus(t: LiveTask, f: StatusFilter): boolean {
  if (f === 'all') return true;
  if (f === 'open') return isOpenTask(t);
  return !isOpenTask(t); // 'done' — anything not open
}

export default function AllTasks() {
  const { role } = useAuth();
  const { toast } = useToast();
  const [tasks, setTasks] = useState<LiveTask[]>([]);
  const [loading, setLoading] = useState(true);
  // A failed READ (e.g. permission-denied) must NOT look like a genuinely
  // empty collection — the empty state would falsely read "no tasks".
  const [loadError, setLoadError] = useState(false);
  const [status, setStatus] = useState<StatusFilter>('open');
  const [assignee, setAssignee] = useState<string>('all');

  useEffect(() => {
    const unsub = listenTasks(setTasks, {
      onErr: () => {
        setLoadError(true);
        toast('Could not load tasks (check connection)');
      },
      onReady: () => setLoading(false),
    });
    return unsub;
  }, [toast]);

  // Build B is an admin oversight screen — re-check the role even though
  // the route is admin-gated (defence in depth, same as Workload.tsx).
  if (!visibilityFor(role).seesCrossProject) {
    return (
      <Box title="All Tasks" note="Restricted">
        <EmptyNote title="Not available on this track">
          The cross-team task-oversight view is admin-only. Your own tasks
          appear on your Home screen.
        </EmptyNote>
      </Box>
    );
  }

  // Filter, then group. The assignee filter is applied to the full set so
  // the assignee <Pill> list reflects everyone with tasks regardless of the
  // current status filter.
  const visible = useMemo(
    () =>
      tasks.filter(
        (t) =>
          matchesStatus(t, status) &&
          (assignee === 'all' || t.assigneeUid === assignee),
      ),
    [tasks, status, assignee],
  );
  const groups = useMemo(() => groupByAssignee(visible), [visible]);
  // Assignee filter options come from ALL tasks (status-independent).
  const allAssignees = useMemo(() => groupByAssignee(tasks), [tasks]);
  const totalOpen = openCount(tasks);

  return (
    <Box
      title="All Tasks"
      note={`${tasks.length} task${tasks.length === 1 ? '' : 's'} · ${totalOpen} open · created by the AI assistant (in-app + WhatsApp)`}
    >
      {/* ── filters ── */}
      <div
        style={{
          display: 'flex',
          gap: 8,
          flexWrap: 'wrap',
          alignItems: 'center',
          marginBottom: 16,
        }}
      >
        {(
          [
            ['open', 'Open'],
            ['done', 'Done'],
            ['all', 'All'],
          ] as Array<[StatusFilter, string]>
        ).map(([k, label]) => (
          <Pill key={k} on={status === k} onClick={() => setStatus(k)}>
            {label}
          </Pill>
        ))}
        <span
          aria-hidden
          style={{ width: 1, height: 20, background: 'var(--rule)' }}
        />
        <Pill on={assignee === 'all'} onClick={() => setAssignee('all')}>
          Everyone
        </Pill>
        {allAssignees.map((g) => (
          <Pill
            key={g.assigneeUid || 'unassigned'}
            on={assignee === g.assigneeUid}
            onClick={() => setAssignee(g.assigneeUid)}
          >
            {g.assigneeName} · {g.openCount}
          </Pill>
        ))}
      </div>

      {loading && <ListLoading label="Loading tasks…" />}

      {!loading && loadError && (
        <EmptyNote title="Tasks could not be loaded">
          Something went wrong reading the task list — this is not the same
          as having no tasks. Check your connection and refresh; if it
          persists, the task collection's access rules may need attention.
        </EmptyNote>
      )}

      {!loading && !loadError && tasks.length === 0 && (
        <EmptyNote title="No tasks yet">
          The AI assistant creates tasks here when someone asks it to —
          in the portal chat or over WhatsApp. None have been created yet.
        </EmptyNote>
      )}

      {!loading && tasks.length > 0 && groups.length === 0 && (
        <EmptyNote title="No tasks match this filter">
          Try a different status or assignee filter.
        </EmptyNote>
      )}

      {!loading && groups.length > 0 && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
          {groups.map((g) => (
            <AssigneeBlock
              key={g.assigneeUid || 'unassigned'}
              name={g.assigneeName}
              uid={g.assigneeUid}
              openN={g.openCount}
              tasks={g.tasks}
            />
          ))}
        </div>
      )}
    </Box>
  );
}

/* ──────────── one assignee's task block ──────────── */

function AssigneeBlock({
  name,
  uid,
  openN,
  tasks,
}: {
  name: string;
  uid: string;
  openN: number;
  tasks: LiveTask[];
}) {
  return (
    <div>
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 10,
          marginBottom: 8,
        }}
      >
        <b style={{ fontSize: 14.5 }}>{name}</b>
        {uid === UNASSIGNED_UID && <Tag kind="warn">unassigned</Tag>}
        <span style={{ fontSize: 12, color: 'var(--ink-mute)' }}>
          {tasks.length} task{tasks.length === 1 ? '' : 's'} · {openN} open
        </span>
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        {tasks.map((t) => (
          <TaskRow key={t.id} t={t} />
        ))}
      </div>
    </div>
  );
}

function TaskRow({ t }: { t: LiveTask }) {
  const open = isOpenTask(t);
  const when =
    t.atMs != null
      ? new Date(t.atMs).toLocaleDateString([], {
          day: 'numeric',
          month: 'short',
        })
      : null;
  // Plain-language origin chip: where the assistant was driven from.
  const via =
    t.surface === 'whatsapp'
      ? 'WhatsApp'
      : t.surface === 'portal_chat'
        ? 'Portal chat'
        : t.createdVia || t.source || 'AI assistant';
  return (
    <Card flat className="row-card" style={open ? undefined : { opacity: 0.62 }}>
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 10,
          flexWrap: 'wrap',
        }}
      >
        <b style={{ fontSize: 13.5 }}>{t.title}</b>
        <Tag kind={t.priority === 'high' ? 'warn' : 'tag'}>{t.priority}</Tag>
        <Tag kind={open ? 'val' : 'default'}>{open ? 'open' : t.status}</Tag>
        <span style={{ fontSize: 12, color: 'var(--ink-mute)' }}>
          via {via}
        </span>
        {when && (
          <span style={{ marginLeft: 'auto', fontSize: 12, color: 'var(--ink-mute)' }}>
            {when}
          </span>
        )}
      </div>
    </Card>
  );
}
